/**
 * End shelves (claude-docs/2026-10-30-venus-full-line-design.md, group 3e):
 * a bay's front (`type: 'end-shelf'`) — open shelves curving round a run's
 * open end — the one rule the panel, the fill, the drawing and the SKU read.
 *
 * The user (2026-10-31) took the END BAY: a 12" bay at an end of the run
 * wears it, rather than a unit added past the run's end. Its OPEN side is the
 * side no cabinet stands against (`endShelfSideFor`, as seen facing it: its
 * local +X is the viewer's right), stamped on the cabinet by the fill
 * (`endShelfSide`) so the part and the drawing need no room to read it.
 *
 * Venus sells one in a base, `BES12L` / `BES12R` (12" x 34½" x 24", "Left
 * Open" / "Right Open", catalog p.11), and one on the wall, `OE1230` / `36` /
 * `42` (a 12" x 12" quarter round, catalog p.13). A generic line offers the
 * same sizes. A bay of any other size, or one with cabinets on both sides,
 * says why.
 *
 * Pure.
 */
const { unitsOf, familySizes } = require('./vendorSizes');
const { familyOf } = require('./families');
const { objectsAreTouching } = require('../geometry/objectsTouch');

const IN = 0.0254;
const WIDTH_IN = 12;
const TOL_IN = 1 / 16;
// a neighbour this close stands against a side (the trim's touch tolerance)
const TOUCH_M = 0.01;

/** The generic sizes, for every line with no end shelves of its own. */
const GENERIC_END_SHELVES = Object.freeze({
  base: Object.freeze([{ w: 12, h: 34.5, d: 24 }]),
  wall: Object.freeze([
    { w: 12, h: 30, d: 12 },
    { w: 12, h: 36, d: 12 },
    { w: 12, h: 42, d: 12 },
  ]),
});

const KIND = Object.freeze({ base: 'base-end-shelf', wall: 'wall-end-shelf' });

/** Is this cabinet an end shelf? */
const isEndShelf = (o) => o?.category?.endShelf === true;

/** The tier a bay is in: an upper's is the wall. */
const tierOf = (o) =>
  o?.type === 'upper-cabinet' || o?.category?.['upper-cabinet']
    ? 'wall'
    : 'base';

/**
 * The end shelves a line sells in a tier: `{ sku, w, h, d, open? }` — a
 * vendor's from its catalog, a generic line's the same sizes with no part.
 */
function endShelfUnits(family, tier) {
  if (familySizes(family))
    return unitsOf(family, KIND[tier]).map((u) => ({
      sku: u.sku,
      w: u.w,
      h: u.h,
      d: u.d,
      ...(u.open ? { open: u.open } : {}),
    }));
  return GENERIC_END_SHELVES[tier].map((g) => ({ sku: null, ...g }));
}

/**
 * Which sides of this cabinet something stands against, `{ left, right }`
 * as seen facing it: any of `objects` touching it whose centre lies beside
 * it (not behind or in front), a countertop aside.
 */
function neighbourSides(cabinet, objects) {
  const ry = cabinet.rotation?.y || 0;
  const along = { x: Math.cos(ry), z: -Math.sin(ry) };
  const sides = { left: false, right: false };
  for (const o of objects || []) {
    if (o === cabinet || o.id === cabinet.id || !o?.center || !o.dimensions)
      continue;
    if (o.type === 'countertop') continue;
    if (!objectsAreTouching(cabinet, o, TOUCH_M)) continue;
    const ax =
      (o.center.x - cabinet.center.x) * along.x +
      (o.center.z - cabinet.center.z) * along.z;
    // only what stands beside it, not behind or in front
    if (Math.abs(ax) < cabinet.dimensions.x / 2 - TOUCH_M) continue;
    sides[ax > 0 ? 'right' : 'left'] = true;
  }
  return sides;
}

/**
 * Which side of this cabinet stands open — `'left'` / `'right'` as seen
 * facing it — or null when something stands against both sides or neither.
 */
function endShelfSideFor(cabinet, objects) {
  const sides = neighbourSides(cabinet, objects);
  if (sides.left === sides.right) return null;
  return sides.left ? 'right' : 'left';
}

const said = (m) => `${Math.round((m / IN) * 8) / 8}"`;
const listText = (xs) =>
  xs.length > 1
    ? `${xs.slice(0, -1).join(', ')} or ${xs[xs.length - 1]}`
    : xs.join('');

/**
 * Why this bay cannot wear an end shelf, or null when it can: 12" wide, a
 * size the line makes, at an end of its run (`objects`, the room's, find
 * its neighbours; without them the end goes unasked).
 */
function endShelfReason(run, cabinet, objects) {
  const d = cabinet?.dimensions;
  if (!(d?.x > 0)) return null;
  if (Math.abs(d.x / IN - WIDTH_IN) > TOL_IN)
    return `An end shelf is ${WIDTH_IN}" wide: this bay is ${said(d.x)}`;
  const family = familyOf(run);
  const tier = tierOf(cabinet);
  const units = endShelfUnits(family, tier);
  if (units.length === 0) return `${family.name} sells no ${tier} end shelf`;
  if (tier === 'wall' && !units.some((u) => Math.abs(u.h - d.y / IN) <= 0.5))
    return `${family.name} makes its wall end shelf ${listText(units.map((u) => `${u.h}"`))} tall: this bay is ${said(d.y)}`;
  if (tier === 'base' && !units.some((u) => Math.abs(u.d - d.z / IN) <= 1))
    return `${family.name} makes its base end shelf ${units[0].d}" deep: this bay is ${said(d.z)}`;
  if (objects) {
    const sides = neighbourSides(cabinet, objects);
    if (sides.left && sides.right)
      return 'An end shelf closes a run’s end: this bay has cabinets on both sides';
    if (!sides.left && !sides.right)
      return 'An end shelf closes a run’s end against its last cabinet: this bay stands alone';
  }
  return null;
}

/**
 * Stamp each end shelf among `objects` with the side it stands open on
 * (`endShelfSide`), from what stands beside it among the same objects — the
 * fill's last pass, so the part (`BES12L` / `BES12R`) and the drawing read
 * it off the cabinet. One with no single open side carries none. Mutates the
 * end shelves only.
 */
function stampEndShelfSides(objects) {
  for (const o of objects) {
    if (!isEndShelf(o)) continue;
    const side = endShelfSideFor(o, objects);
    if (side) o.endShelfSide = side;
    else delete o.endShelfSide;
  }
}

module.exports = {
  END_SHELF_WIDTH_IN: WIDTH_IN,
  stampEndShelfSides,
  GENERIC_END_SHELVES,
  isEndShelf,
  endShelfUnits,
  endShelfSideFor,
  endShelfReason,
  neighbourSides,
};
